import { makePersisted } from "@solid-primitives/storage";
import localforage from "localforage";
import {
  createContext,
  createMemo,
  type ParentComponent,
  useContext,
} from "solid-js";
import { createStore, produce } from "solid-js/store";
import { isServer } from "solid-js/web";
import type { JobOfferV2 } from "@/domains/job-offer/types";

type VisitedJob = Pick<JobOfferV2, "id">;

type InitialState = {
  visitedJobs: VisitedJob[];
};

const createVisitedJobStore = (initialState: InitialState) => {
  const DEFAULT_STATE: InitialState = {
    visitedJobs: initialState.visitedJobs,
  };

  return makePersisted(createStore<InitialState>(DEFAULT_STATE), {
    storage: isServer ? undefined : localforage,
    name: "visited-jobs",
  });
};

const VisitedJobContext =
  createContext<ReturnType<typeof createVisitedJobStore>>();

export const VisitedJobProvider: ParentComponent<{
  initialState: InitialState;
}> = (props) => {
  const store = createVisitedJobStore({
    visitedJobs: props.initialState.visitedJobs,
  });

  return (
    <VisitedJobContext.Provider value={store}>
      {props.children}
    </VisitedJobContext.Provider>
  );
};

export const useVisitedJobStore = () => {
  const store = useContext(VisitedJobContext);
  if (!store) {
    throw new Error("VisitedJobContext not found");
  }
  const [visitedJobs, setVisitedJobs] = store;
  const isJobVisited = (jobId: string) => {
    return visitedJobs.visitedJobs.some(
      (visitedJob) => visitedJob.id === jobId,
    );
  };
  const addVisitedJob = (jobId: string) => {
    setVisitedJobs(
      produce((visitedJobs) => {
        visitedJobs.visitedJobs.push({
          id: jobId,
        });
      }),
    );
  };

  const value = createMemo(() => ({
    visitedJobs,
    setVisitedJobs,
    isJobVisited,
    addVisitedJob,
  }));
  return value();
};

export const clearVisitedJobStore = async () => {
  await localforage.removeItem("visited-jobs");
};
